import { useState } from "react";
import { LogOut, PanelLeftClose, PanelLeftOpen, Settings } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { LogoLettersBlack, BunnyEarsLogo } from "../../../assets/images";
import { logoutFirebase } from "../../../auth/operations";
import { HOME } from "../../../utils/routes";
import CustomModal from "../../CustomModal";
import DashboardModal from "../../DashboardModal";
import { PartnerCapabilities } from "../capabilities";
import { SectionDef, SectionKey, visibleSections } from "../sections";
import {
  Aside,
  Backdrop,
  LogoRow,
  LogoImg,
  LogoMark,
  CollapseButton,
  NavList,
  NavItem,
  ItemLabel,
  Footer,
  FooterButton,
  LogoutButton,
} from "./styles";

interface PartnerSidebarProps {
  active: SectionKey;
  onSelect: (key: SectionKey) => void;
  capabilities: PartnerCapabilities;
  multiLocation: boolean;
  open: boolean;
  onClose: () => void;
  collapsed: boolean;
  onToggleCollapsed: () => void;
}

const PartnerSidebar = ({
  active,
  onSelect,
  capabilities,
  multiLocation,
  open,
  onClose,
  collapsed,
  onToggleCollapsed,
}: PartnerSidebarProps) => {
  const [showSettings, setShowSettings] = useState(false);
  const navigate = useNavigate();
  const sections = visibleSections(capabilities, multiLocation);

  const handleSelect = (key: SectionKey) => {
    onSelect(key);
    onClose();
  };

  const handleLogout = async () => {
    await logoutFirebase();
    navigate(HOME);
  };

  return (
    <>
      <Backdrop $open={open} onClick={onClose} />
      <Aside $open={open} $collapsed={collapsed} aria-label="Partner dashboard navigation">
        <LogoRow $collapsed={collapsed}>
          {collapsed ? (
            <LogoMark src={BunnyEarsLogo} alt="JackedRabbit" />
          ) : (
            <LogoImg src={LogoLettersBlack} alt="JackedRabbit" />
          )}
          <CollapseButton
            onClick={onToggleCollapsed}
            aria-label={collapsed ? "Expand menu" : "Collapse menu"}
            title={collapsed ? "Expand menu" : "Collapse menu"}
          >
            {collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
          </CollapseButton>
        </LogoRow>

        <NavList>
          {sections.map((s: SectionDef) => {
            const Icon = s.icon;
            return (
              <NavItem
                key={s.key}
                $active={active === s.key}
                $collapsed={collapsed}
                onClick={() => handleSelect(s.key)}
                aria-current={active === s.key ? "page" : undefined}
                aria-label={collapsed ? s.label : undefined}
                title={collapsed ? s.label : undefined}
              >
                <Icon size={18} strokeWidth={active === s.key ? 2.4 : 2} />
                <ItemLabel $collapsed={collapsed}>{s.label}</ItemLabel>
              </NavItem>
            );
          })}
        </NavList>

        <Footer>
          <FooterButton
            $collapsed={collapsed}
            onClick={() => setShowSettings(true)}
            aria-label={collapsed ? "Settings" : undefined}
            title={collapsed ? "Settings" : undefined}
          >
            <Settings size={18} />
            <ItemLabel $collapsed={collapsed}>Settings</ItemLabel>
          </FooterButton>
          <LogoutButton
            $collapsed={collapsed}
            onClick={handleLogout}
            aria-label={collapsed ? "Log out" : undefined}
            title={collapsed ? "Log out" : undefined}
          >
            <LogOut size={16} />
            <ItemLabel $collapsed={collapsed}>Log out</ItemLabel>
          </LogoutButton>
        </Footer>
      </Aside>

      <CustomModal
        showModal={showSettings}
        onClose={() => setShowSettings(false)}
        content={<DashboardModal />}
        width="420px"
        padding="10px 0px 0px 0px"
        backgroundColor="#ffffff"
        closeColor="#555555"
      />
    </>
  );
};

export default PartnerSidebar;
